<template>
  <div class="filters">
    <v-menu
      v-model="menu"
      :disabled="disabled"
      :close-on-content-click="false"
      :min-width="500"
      :max-width="500"
      :max-height="500"
      :z-index="menuZindex"
      offset="8"
      attach
      contained
      @update:model-value="closeModal"
    >
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          :ripple="false"
          :min-width="100"
          :style="filterButtonStyle"
          :height="28"
          :disabled="disabled"
          variant="flat"
          :class="{
            primary: menu,
            secondary: !menu,
          }"
        >
          {{ btnText }}
        </v-btn>
      </template>
      <v-card :min-width="500" :max-width="500" :max-height="500">
        <div class="pa-4 d-flex flex-column">
          <div class="d-flex flex-column">
            <div class="mb-2">
              <v-select
                v-model="filterValue"
                :items="filterOptionsUniqueValues"
                :menu-props="{
                  contentClass: 'options',
                  location: 'bottom center',
                }"
                item-title="name"
                return-object
                hide-details
                class="mt-1 filter-select"
                :menu-icon="null"
                @update:model-value="selectedFilterValues = []"
              >
                <template #append-inner>
                  <v-icon role="img" aria-hidden="false" size="10" icon="rtls:BaseIconExpandMore" />
                </template>
                <template #item="data">
                  <v-list-item v-bind="data.props" class="slot-item" />
                </template>
              </v-select>
            </div>
            <div class="mb-1 d-flex align-center justify-start">
              <v-checkbox
                class="rtls-checkbox mx-2"
                color="primary"
                density="compact"
                hide-details
                :model-value="notEqualTo"
                @update:model-value="notEqualTo = $event"
              >
                <template #label>
                  <div>
                    <h3>{{ tkey('notEqual') }}</h3>
                  </div>
                </template>
              </v-checkbox>
            </div>
            <div class="mb-4 d-flex align-center">
              <v-autocomplete
                ref="autocompleteSelect"
                v-model="selectedFilterValues"
                v-model:search="searchInput"
                :items="selectedFilterValueOptions"
                chips
                item-title="name"
                :multiple="selectedFilterDefinition && selectedFilterDefinition.allowSelectMultiple"
                bg-color="white"
                density="compact"
                hide-details
                hide-selected
                return-object
                :menu-props="{ closeOnClick: true }"
                @update:model-value="searchInput = ''"
                @click:append="menuArrow"
              >
                <template #chip="{ item }">
                  <v-chip
                    :title="item.title"
                    size="small"
                    grey
                    closable
                    class="slot-chip"
                    @click:close="deleteChip(item)"
                  >
                    {{ item.title }}
                  </v-chip>
                </template>
                <template #item="data">
                  <v-list-item v-bind="data.props" class="slot-item slot-item-autocomplete" />
                </template>
              </v-autocomplete>
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    variant="text"
                    :disabled="isAddFilterDisabled"
                    class="ml-3 add-filter-button"
                    @click="addFilter"
                  >
                    <v-icon primary size="24"> mdi-plus-box </v-icon>
                  </v-btn>
                </template>
                <span>{{
                  isAddFilterDisabled ? tkey('selectAValueToAddFilter') : tkey('addFilter')
                }}</span>
              </v-tooltip>
            </div>
            <div class="mb-5 d-flex flex-column">
              <div v-if="filters.length" class="d-flex flex-column">
                <h2 class="title-filters">
                  {{ tkey('appliedFilters') }}
                </h2>
                <div class="d-flex">
                  <v-col class="headers pr-0 pb-1">
                    <h4 class="pb-0">
                      {{ tkey('filterName') }}
                    </h4>
                  </v-col>
                  <v-col class="headers pr-0 pb-1">
                    <h4 class="pb-0">
                      {{ tkey('equalNotEqual') }}
                    </h4>
                  </v-col>
                  <v-col class="headers pr-0 pb-1">
                    <h4 class="pb-0">
                      {{ tkey('filterValues') }}
                    </h4>
                  </v-col>
                  <v-col :cols="1" class="headers pb-1 pr-1" />
                </div>
                <div v-for="(filter, index) in filters" :key="filter.id" class="d-flex filter-row">
                  <v-col class="filter-item pr-0 pt-1 pb-1 d-flex flex-column justify-center">
                    {{ filter.filterName }}
                  </v-col>
                  <v-col class="filter-item pr-0 pt-1 pb-1 d-flex flex-column justify-center">
                    {{ filter.notEqualTo ? tkey('notEqual') : tkey('equal') }}
                  </v-col>
                  <v-col class="filter-item pr-0 pt-1 pb-1 d-flex flex-column justify-center">
                    <div v-for="value in filter.filterValues" :key="value.value + value.index">
                      {{ value.name || value }}
                    </div>
                  </v-col>
                  <v-col
                    :cols="1"
                    class="filter-item pt-1 pb-1 pr-1 d-flex flex-column justify-center align-center"
                  >
                    <v-btn icon size="24" class="ml-1" variant="text" @click="removeFilter(index)">
                      <v-icon size="small" primary> mdi-close-circle </v-icon>
                    </v-btn>
                  </v-col>
                </div>
              </div>
              <div v-else>
                <h3>{{ tkey('noFiltersApplied') }}</h3>
              </div>
            </div>
          </div>
          <div class="d-flex justify-end align-center close-icon-container">
            <v-btn
              class="mr-0 font-weight-bold btn-text"
              :ripple="false"
              variant="text"
              size="small"
              @click="[(menu = false), closeModal(false)]"
            >
              {{ t('actions.close') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { ref } from 'vue';
import { isArray, isPlainObject, isEqual, uniqBy } from 'lodash';
import { v4 as uuidV4 } from 'uuid';
import assortmentCanvases from '@enums/assortment-canvases';
import menus from '@enums/menus';
import { useInternationalization } from '@/composables/use-internationalization';

export default {
  props: {
    filters: {
      required: true,
      type: Array,
    },
    filterOptions: {
      required: true,
      type: Array,
    },
    btnText: {
      required: true,
      type: String,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  emits: ['reset', 'change', 'apply'],

  setup() {
    const notEqualTo = ref(false);
    const searchInput = ref('');
    const menu = ref(false);
    const selectedFilterValues = ref([]);
    const previouslyAppliedFilters = ref([]);
    const menuZindex = ref(menus.zIndexes.menu);
    const { t, tkey } = useInternationalization('filters');
    const filterValue = ref({ name: t('general.select'), type: null });

    return {
      t,
      tkey,
      notEqualTo,
      searchInput,
      menu,
      filterValue,
      selectedFilterValues,
      previouslyAppliedFilters,
      menuZindex,
    };
  },

  computed: {
    selectedFilterDefinition() {
      return this.filterOptionsUniqueValues.find(fi => fi.type === this.filterValue.type);
    },

    selectedFilterValueOptions() {
      if (!this.selectedFilterDefinition) return [];
      if (this.selectedFilterDefinition.valuesGetter)
        return this.selectedFilterDefinition.valuesGetter(this.filterValue);
      return this.selectedFilterDefinition.values;
    },

    isAddFilterDisabled() {
      // disable add filter button if no values are selected.
      // accounts for when autocomplete doesn't allow multiple selections, in that case the property is an object
      const isSingleValue = isPlainObject(this.selectedFilterValues);
      // accounts for multiple selections when property is an array
      const areMultipleValues = isArray(this.selectedFilterValues);
      if (isSingleValue) {
        return false;
      }
      if (areMultipleValues) {
        return !this.selectedFilterValues.length;
      }
      return true;
    },

    filterButtonStyle() {
      // Add padding to the main button when the menu opens, as it changes from secondary --> primary button
      // Makes sure UI elements stay in consistent place
      return this.menu ? { 'padding-right': '18px' } : {};
    },

    filterOptionsUniqueValues() {
      return this.filterOptions.map(option => ({
        ...option,
        values: uniqBy(option.values, 'name'),
      }));
    },
  },

  created() {
    this.reset();
  },

  methods: {
    reset() {
      this.$emit('reset');
    },

    menuArrow() {
      // allow arrow icon to close autocomplete selection menu
      const autocompleteSelect = this.$refs.autocompleteSelect;
      if (autocompleteSelect.isMenuActive) {
        this.$refs.autocompleteSelect.isMenuActive = false;
        autocompleteSelect.blur();
      } else {
        this.$refs.autocompleteSelect.isMenuActive = true;
        autocompleteSelect.focus();
      }
    },

    addFilter() {
      const newFilter = {
        id: uuidV4(),
        notEqualTo: this.notEqualTo,
        filterType: this.filterValue.type,
        path: this.filterValue.path,
        // Need to add the attributeIds for both attributes as there are scenarios where attributes
        // can have the same name but different ids when comparing across different scenarios.
        attributeIds:
          this.filterValue.type === assortmentCanvases.Filters.customAttributes
            ? this.filterValue.attributeIds || [this.filterValue.id]
            : '',
        filterName: this.filterValue.name,
        filterValues: isArray(this.selectedFilterValues)
          ? this.selectedFilterValues
          : [this.selectedFilterValues],
      };
      this.selectedFilterValues = [];
      this.filterValue = { name: null, type: null };
      this.notEqualTo = false;
      this.$emit('change', [...this.filters, newFilter]);
    },

    removeFilter(index) {
      this.$emit('change', [...this.filters.slice(0, index), ...this.filters.slice(index + 1)]);
    },

    deleteChip(item) {
      if (isArray(this.selectedFilterValues)) {
        const index = this.selectedFilterValues.indexOf(item);
        this.selectedFilterValues.splice(index, 1);
      } else {
        this.selectedFilterValues = null;
      }
    },

    differenceInFilters(previous, current) {
      const previousIds = previous.map(({ id }) => id);
      const currentIds = current.map(({ id }) => id);
      return !isEqual(previousIds, currentIds);
    },

    closeModal() {
      if (!this.menu && this.differenceInFilters(this.previouslyAppliedFilters, this.filters)) {
        this.previouslyAppliedFilters = this.filters;
        this.$emit('apply');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.close-icon-container {
  width: 100%;
}

.btn-text {
  color: $assortment-primary-colour;
  padding-right: 0 !important;

  &::before {
    opacity: 0 !important;
  }
}

.rtls-checkbox {
  margin: 0 !important;

  .v-input--selection-controls__input {
    margin-right: 0 !important;
  }

  :deep(.v-label) {
    opacity: unset;
  }

  :deep(.v-icon) {
    font-size: 20px;
    font-weight: 400;
    color: #2f477c;
    opacity: unset;
  }
}

:deep(.v-btn:not(.v-btn--text):not(.v-btn--outlined):focus:before) {
  opacity: 0 !important;
  border-radius: 0;
}

:deep(.v-chip) {
  padding-right: 30px;
}

:deep(.v-chip__content) {
  // set max-width on the chip content so it doesn't overflow the container
  max-width: 300px;
  height: auto;
  display: inline-block !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.v-chip__close) {
  position: absolute;
  top: 3px;
  right: 12px;
  width: 24px;
}

.title-filters {
  padding-left: 5px;
}

.filter-item {
  font-size: 1.2rem;
  padding-left: 5px;
}

.filter-row {
  border-bottom: 1px solid $assortment-divider-colour;
  &:nth-child(odd) {
    background: $assortment-table-blue-bg-colour;
  }
}

.add-filter-button {
  margin-right: 3px;
}

.headers {
  border-bottom: 1px solid $assortment-divider-colour-darker;
  padding-left: 5px;
}

.slot-item {
  width: 452px !important;
  min-height: 32px !important;
  &.slot-item-autocomplete {
    width: 416px !important;
  }
}

.slot-chip {
  :deep(.v-chip__content) {
    margin-right: 20px;
    font-size: 1.2rem;
  }
}

.rtls-select {
  .v-input__slot,
  .v-select__slot {
    background: white;
    height: 32px !important;
  }
  &.select {
    border-radius: 0px;
    height: 32px;
    .v-input__control {
      min-height: 32px;
    }
  }
}

:deep(.filter-select) {
  .v-icon {
    opacity: unset;
  }

  .v-input__control {
    height: 32px;
  }

  .v-field__field {
    height: 32px;
  }

  .v-field__input {
    min-height: 32px;
    padding-top: 8px;
  }

  .v-list {
    padding: 0 !important;
  }

  v-field--focused {
    background-color: red !important;
  }

  .v-field--focused.v-field--variant-filled .v-field__outline::after,
  .v-field--focused.v-field--variant-underlined .v-field__outline::after {
    border-color: $assortment-primary-colour !important;
  }

  &.filter-autocomplete {
    :deep(.v-field__input) {
      min-height: 32px;
      padding-top: 4px !important;
    }
  }
}

.rtls-select {
  .v-input__slot,
  .v-select__slot {
    background: white;
    height: 32px !important;
  }
  &.select {
    border-radius: 0px;
    height: 32px;
    .v-input__control {
      min-height: 32px;
    }
  }
}
</style>
